import { useCallback, useMemo, useRef, useState } from 'react';
import ChevronDownIcon from '../assets/icons/chevron_down.svg?react';
import CloseIcon from '../assets/icons/close.svg?react';
import SearchIcon from '../assets/icons/search.svg?react';
import { cn } from '../utils/className';
import { BottomSheet } from './BottomSheet';
import { Checkbox } from './Checkbox';
import { SearchableSelectOption } from './SearchableSelect';

export interface MobileSearchableSelectProps {
  options: SearchableSelectOption[];
  selectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  mode?: 'single' | 'multi';
  disabled?: boolean;
  label?: string;
}

/**
 * Mobile-optimized searchable select that opens in a bottom sheet.
 * Provides a better experience on touch devices where popovers can jump around.
 */
export function MobileSearchableSelect({
  options,
  selectedValues = [],
  onSelectionChange,
  placeholder = 'Select...',
  className,
  mode = 'multi',
  disabled = false,
  label,
}: MobileSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const selected = options.find((opt) => opt.value === selectedValues[0]);
      return selected?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, placeholder]);

  const handleToggleOption = useCallback(
    (value: string) => {
      if (!onSelectionChange) return;

      if (mode === 'single') {
        onSelectionChange([value]);
        setOpen(false);
        setSearchQuery('');
      } else {
        const newSelection = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        onSelectionChange(newSelection);
      }
    },
    [mode, onSelectionChange, selectedValues]
  );

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  const hasSelectedValues = selectedValues.length > 0;

  return (
    <>
      {/* Trigger button */}
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(
          `flex min-h-11 w-full items-center justify-between gap-2 rounded
          border px-3 py-2.5`,
          'size-p2 font-regular',
          'transition-colors outline-none',
          'focus-visible:border-border-focus',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer',
          !hasSelectedValues &&
            'border-border-primary bg-surface-primary text-tertiary',
          hasSelectedValues &&
            'border-border-focus bg-surface-primary text-focus',
          className
        )}
      >
        <span className='truncate'>{displayText}</span>
        <span
          className='text-secondary flex h-4 w-4 shrink-0 items-center
            justify-center [&_path]:fill-current [&_svg]:fill-current'
        >
          <ChevronDownIcon />
        </span>
      </button>

      {/* Bottom Sheet with options */}
      <BottomSheet open={open} onOpenChange={handleOpenChange} title={label}>
        <div className='flex h-full flex-col'>
          {/* Search input - no border/outline */}
          <div className='bg-surface-primary-support px-4 py-3'>
            <div className='flex items-center gap-2'>
              <span
                className='text-tertiary flex h-4 w-4 shrink-0 items-center
                  justify-center [&_path]:fill-current [&_svg]:fill-current'
              >
                <SearchIcon />
              </span>
              <input
                ref={searchInputRef}
                type='text'
                placeholder='Search...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='size-p2 text-primary placeholder:text-disabled flex-1
                  bg-transparent outline-none'
              />
              {searchQuery && (
                <button
                  type='button'
                  onClick={() => setSearchQuery('')}
                  className='text-tertiary hover:text-primary flex h-4 w-4
                    shrink-0 cursor-pointer items-center justify-center
                    transition-colors [&_path]:fill-current
                    [&_svg]:fill-current'
                  aria-label='Clear search'
                >
                  <CloseIcon className='h-3 w-3' />
                </button>
              )}
            </div>
          </div>

          {/* Options list - scrollable, takes remaining space */}
          <div className='min-h-0 flex-1 overflow-y-auto'>
            {filteredOptions.length === 0 ? (
              <div className='size-p2 text-secondary p-4 text-center'>
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type='button'
                    onClick={() => handleToggleOption(option.value)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3',
                      'transition-colors outline-none',
                      'active:bg-surface-focus-support',
                      isSelected
                        ? 'bg-surface-focus-support'
                        : 'bg-surface-primary'
                    )}
                  >
                    {mode === 'multi' && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleOption(option.value)}
                        tabIndex={-1}
                      />
                    )}
                    <div
                      className='flex min-w-0 flex-1 items-center
                        justify-between'
                    >
                      <span className='size-p2 text-primary truncate'>
                        {option.label}
                      </span>
                      {option.description && (
                        <span className='size-caption text-tertiary truncate'>
                          {option.description}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer - always visible to prevent layout shifts */}
          {mode === 'multi' && (
            <div
              className='border-border-secondary bg-surface-primary flex
                flex-col gap-3 border-t py-3'
            >
              {/* Selection count and clear */}
              <div className='flex items-center justify-between px-5'>
                <span className='size-p2 text-secondary'>
                  {selectedValues.length > 0
                    ? `${selectedValues.length} selected`
                    : 'No selection'}
                </span>
                {selectedValues.length > 0 && (
                  <button
                    type='button'
                    onClick={() => onSelectionChange?.([])}
                    className='size-p2 text-focus font-medium'
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Confirm button */}
              <button
                type='button'
                onClick={() => handleOpenChange(false)}
                className='bg-focus hover:bg-focus-hover mx-4 rounded-lg py-3
                  text-center font-medium text-white transition-colors'
              >
                Done
              </button>
            </div>
          )}
        </div>
      </BottomSheet>
    </>
  );
}
